import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import Event from '../Event'
import { MerchantListRecord } from '@paytrace/pt-skel/type'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import { AppCtx } from '@paytrace/pt-skel/Context'
import {
  TransactionHistorySearchFilters,
  TransactionHistorySearch as TransactionHistorySearchReq,
} from '@paytrace/pt-sdk'

// should update the skel to add the tooltip to the grouped imports in index file
// since it looks like the standard way here
import ToolTip from '@paytrace/pt-skel/Component/ToolTip'
import { HelpIcon } from '@paytrace/pt-skel/Component/Icon'

import { SearchFilterHelp } from '../Constants/HelpText'
import { InputSelect } from './InputSelect'

import { TransactionSearchOptions } from '../Type/ViewTransaction'
import {
  CustomerSelect,
  CustomInput,
  DateTimeRange,
  Fieldset,
  If,
  Legend,
  SearchButton,
  SelectOption,
} from '@paytrace/pt-skel/Component'

const self = Symbol('ViewTransactionForm')

const CSCRespCodes = [
  'Match',
  'No Match',
  'Not Processed',
  'Not Present',
  'Issuer Does Not Support',
]

const AVSRespCodes = [
  'Full Exact Match',
  'Address Match Only',
  'Zip Match Only',
  'No Match',
  'Address Unavailable',
  'Non-US Issuer does not participate',
  'Issuer System Unavailable',
  'Not a Mail/Phone Order',
  'Service Not Supported',
]

const TransactionMethods = [
  'Virtual Terminal / Keyed / Card Not Present',
  'Virtual Terminal / Swiped / Card Present',
  'Virtual Terminal / Chip / Card Present',
  'Virtual Terminal / Fallback Swipe / Card Present',
  'Virtual Terminal / Swiped / Cash Advance',
  'Virtual Terminal / PAX EMV',
  'Recurring Transaction',
  'Trace AR',
  'Payment Link / Shopping Cart',
  'Secure Checkout',
  'Secure Checkout / Swiped / Card Present',
  'Trace AR Checkout',
  'API / Keyed / Card Not Present',
  'API / Swiped / Card Present',
  'API / Swiped / Cash Advance',
  'API / Protect',
  'API / PAX EMV',
  'PayTrace Mobile / Key Entered',
  'PayTrace Mobile / Swiped',
  'PayTrace Mobile / Cash Advance',
  'API / Fallback Swipe / Card Present',
  'API / Chip / Card Present',
]

const TransactionTypes = [
  'Approved',
  'Settled',
  'Pending Settlement',
  'Authorizations',
  'Refunds',
  'Voids',
  'Declines',
  'Store & Forwards',
  'Cash / Check',
]

const FILTER_KEY_MAP: Record<string, keyof TransactionHistorySearchFilters> = {
  Customer: 'customer_filter',
  User: 'user_filter',
  Type: 'type_filter',
  Account: 'account_filter',
  'or Account': 'account_filter',
  Method: 'method_filter',
  AVS: 'avs_resp_filter',
  CSC: 'csc_resp_filter',
}

// move this to shared lib when moving the InputSelect
export interface User { username: string, contact: string }

export interface SearchCriteria {
  searchKey: string
  searchValue: string
}

// traverse the array and fixes "Account" vs "or Account" based on order
export const cleanFilterLabels = (
  filters: Array<SearchCriteria>,
  mainSearch: SearchCriteria,
): Array<SearchCriteria> => {
  let accountKeyExists = mainSearch.searchKey === 'Account'

  return filters.map((filter) => {
    if (filter.searchKey === 'Account' || filter.searchKey === 'or Account') {
      const correctedKey = accountKeyExists ? 'or Account' : 'Account'
      accountKeyExists = true
      return { ...filter, searchKey: correctedKey }
    }
    return filter
  })
}

type BackendFilterKey = keyof TransactionHistorySearchFilters

export const prepareRequest = (
  merchantId: number,
  mainCriteria: SearchCriteria,
  startDate: string, // Format expected: "YYYY-MM-DD"
  startTime: string, // Format expected: "HH:mm:ss"
  endDate: string, // Format expected: "YYYY-MM-DD"
  endTime: string, // Format expected: "HH:mm:ss"
  extraSearchCriteria?: Array<SearchCriteria>,
): TransactionHistorySearchReq => {
  const extra_adv_filters: Record<BackendFilterKey, string[]> = {
    customer_filter: [],
    user_filter: [],
    type_filter: [],
    account_filter: [],
    method_filter: [],
    avs_resp_filter: [],
    csc_resp_filter: [],
  }

  const searchText =
    mainCriteria.searchKey === 'General' ? mainCriteria.searchValue || '' : ''
  const filters = [
    ...(extraSearchCriteria || []),
    ...(mainCriteria.searchKey !== 'General' ? [mainCriteria] : []),
  ]

  filters.forEach(({ searchKey, searchValue }) => {
    if (!searchValue) return

    const backendKey = FILTER_KEY_MAP[searchKey]
    if (backendKey) {
      extra_adv_filters[backendKey] = extra_adv_filters[backendKey] || []
      extra_adv_filters[backendKey].push(searchValue)
    }
  })

  return {
    search_text: searchText,
    merchant_id: merchantId,
    // backend expects the literal local date and time combined provided via search
    start_date: moment(`${startDate}T${startTime}`).format(),
    end_date: moment(`${endDate}T${endTime}`).format(),
    ...extra_adv_filters,
  }
}

const FilterSearchOptionType = {
  User: 'User',
  Customer: 'Customer',
}

const parseAccociatedAccountsToOptions = (accounts: MerchantListRecord[]): SelectOption[] => {
  return accounts.map((acct) => ({
    name: `${acct.merchant_id} / ${acct.merchant_name}`,
    val: acct.merchant_id?.toString() || '',
  }))
}

const parseUsersToOptions = (users: User[]): SelectOption[] => {
  return users.map((u) => ({
    name: `${u.username} / ${u.contact || ''}`,
    val: u.username.toString(),
  }))
}

export function TransactionHistorySearchForm (props: {
  onSubmit: (data: TransactionHistorySearchReq) => any
}) {
  const helpEnabled = true
  const context = useContext(AppCtx)
  const merchantId = context.merchant
  const merchantName = context.records.merchant?.merchant_name
  const associatedMerchantList = context.records.associatedMerchantList

  const searchOptions: Array<TransactionSearchOptions> = [
    'Account',
    'User',
    'Customer',
    'Type',
    'Method',
    'AVS',
    'CSC',
  ]

  const [searchStartDate, setSearchStartDate] = useState<string>(
    moment().format('YYYY-MM-DD')
  )
  const [searchStartTime, setSearchStartTime] = useState<string>(
    '00:00:00'
  )
  const [searchEndDate, setSearchEndDate] = useState<string>(
    moment().format('YYYY-MM-DD')
  )
  const [searchEndTime, setSearchEndTime] = useState<string>(
    '23:59:59'
  )
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>({
    searchKey: 'General',
    searchValue: '',
  })
  const [extraSearchCriteria, setExtraSearchCriteria] = useState<
    Array<SearchCriteria>
  >([])
  const [disabled, setDisabled] = useState(false)
  // TODO: extract this out to a hook I think. because i am going
  // to need one for "customer" and "merchant accounts", and considering maybe moving
  // the handler into those hooks too just to keep it abstracted out of this component.
  // list of users for all the accounts to use in lookahead suggestion component.
  const [prefetchUsers, setPrefetchUsers] = useState<User[]>([])
  const [isPrefetching, setIsPrefetching] = useState<boolean>(true)

  const prefetchAccountsOptions = useMemo(() => {
    return parseAccociatedAccountsToOptions(associatedMerchantList || [])
  }, [associatedMerchantList])

  const prefetchUsersOptions = useMemo(() => {
    return parseUsersToOptions(prefetchUsers)
  }, [prefetchUsers])

  useEffect(() => {
    PubSub.sub(self, Event.SEARCH_TRANSACTION_HISTORY_REQUEST, async () => {
      setDisabled(true)
    })

    PubSub.sub(self, Event.SEARCH_TRANSACTION_HISTORY_RESPONSE, async () => {
      setDisabled(false)
    })

    const handleUsersResponse = (data: { results: User[] }) => {
      const newResults = data.results || []
      setPrefetchUsers((prev) => {
        const updated = [...prev, ...newResults]
        // make another server request call if there could be more users
        // starting offset at the last length to get the next set of data.
        if (newResults.length === 200) {
          PubSub.pub(Event.GET_USERS_BY_ACCT_REQUEST, {
            name_filter: '',
            offset: updated.length,
            limit: 200,
          })
        } else {
          setIsPrefetching(false)
        }
        return updated
      })
    }

    // publish an event to pre-fetch users. Other options to where this can be
    // triggered, could trigger from search criteria selection change or trigger
    // on the child component mounting.
    PubSub.pub(Event.GET_USERS_BY_ACCT_REQUEST, {
      name_filter: '',
      offset: 0,
      limit: 200,
    })

    PubSub.sub(self, Event.GET_USERS_BY_ACCT_RESPONSE, handleUsersResponse)

    return () => {
      PubSub.unsub(self)
    }
  }, [])

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (disabled) {
      // can polish up the ux later, just do not want user to continually smash the button
      // would be nice to be able to cancel in progress search and button
      // doesn't have disabled property
      alert('please wait, search in progress')
      return
    }

    const payload = prepareRequest(
      Number(merchantId),
      searchCriteria,
      searchStartDate,
      searchStartTime,
      searchEndDate,
      searchEndTime,
      extraSearchCriteria,
    )

    props.onSubmit(payload)
  }

  // this only handles the main search text box
  const handleUpdateSearchCriteria = (
    val: string,
    field: 'searchKey' | 'searchValue',
  ) => {
    const newValue = val
    setSearchCriteria((prev) => {
      // copy the single previous object and apply the new value
      const updated = {
        ...prev,
        [field]: newValue,
      }

      // clear the value if dropdown changes
      if (field === 'searchKey') {
        updated.searchValue = ''
      }

      return updated
    })
  }

  const handleRemoveExtraFilter = (
    e: React.MouseEvent<HTMLButtonElement>, indexToRemove: number
  ) => {
    e.preventDefault()

    setExtraSearchCriteria((prev) => {
      const newList = prev.filter((_, index) => index !== indexToRemove)
      return cleanFilterLabels(newList, searchCriteria)
    })
  }

  const handleAddExtraFilter = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    // can not default to 'General' since only first filter will have that option
    // defaulting to 'Account'.
    // Adding more than one account will make the addition be an "or"
    const criteria: SearchCriteria = { searchKey: 'Account', searchValue: '' }
    setExtraSearchCriteria((prev) => {
      const newList = [...prev, criteria]
      return cleanFilterLabels(newList, searchCriteria)
    })

    return false
  }

  const handleUpdateExtraCriteria = (
    val: string,
    index: number,
    field: 'searchKey' | 'searchValue',
  ) => {
    const newValue = val

    setExtraSearchCriteria((prev) => {
      // same as the single copy but allow multiple so is an array
      const updated = [...prev]

      updated[index] = {
        ...updated[index],
        [field]: newValue,
      }

      // If changed the key, clear out the value so the placeholder shows
      if (field === 'searchKey') {
        updated[index].searchValue = ''
        return cleanFilterLabels(updated, searchCriteria)
      }
      return updated
    })
  }

  const createFilterLabel = (_: SearchCriteria) => {
    // force common select to show empty string to not show default
    return ' '
  }

  // method to create types of filters based on selected search type
  const createSearchFilterSelect = (
    criteria: SearchCriteria,
    onSelectionChange: (val: string) => void
  ) => {
    switch (criteria.searchKey) {
      case FilterSearchOptionType.User:
        return (
          <InputSelect
            handleSelectionChange={(evt) => {
              onSelectionChange(evt.val)
            }}
            selectedItem={criteria?.searchValue}
            items={prefetchUsersOptions}
            isLoading={isPrefetching}
            className='uid-user-select-vt'
            label={createFilterLabel(criteria)}
            placeHolder='-- User ID / Name --'
          />
        )
      case FilterSearchOptionType.Customer:
        return (
          <CustomerSelect
            handleSelectionChange={(evt) => {
              onSelectionChange(evt.val)
            }}
            selectedCustomerId={criteria?.searchValue}
            merchantId={String(merchantId)}
            associatedMerchantList={associatedMerchantList}
            showLegend={false}
            hideFieldset={true}
            className='uid-customer-select-vt'
            label={createFilterLabel(criteria)}
          />
        )
      case 'or Account':
      case 'Account':
        return (
          <InputSelect
            handleSelectionChange={(evt) => {
              onSelectionChange(evt.val)
            }}
            selectedItem={criteria?.searchValue}
            items={prefetchAccountsOptions}
            className='uid-account-select-vt'
            label={createFilterLabel(criteria)}
            placeHolder={'-- Account ID --'}
          />
        )
      case 'Type':
        return (
          <InputSelect
            handleSelectionChange={(evt) => {
              onSelectionChange(evt.val)
            }}
            selectedItem={criteria?.searchValue}
            items={TransactionTypes}
            className='uid-tranxtype-select-vt'
            label={createFilterLabel(criteria)}
            placeHolder={'-- Transaction Type --'}
          />
        )
      case 'Method':
        return (
          <InputSelect
            handleSelectionChange={(evt) => {
              onSelectionChange(evt.val)
            }}
            selectedItem={criteria?.searchValue}
            items={TransactionMethods}
            className='uid-tranxmethods-select-vt'
            label={createFilterLabel(criteria)}
            placeHolder={'-- Transaction Method --'}
          />
        )
      case 'AVS':
        return (
          <InputSelect
            handleSelectionChange={(evt) => {
              onSelectionChange(evt.val)
            }}
            selectedItem={criteria?.searchValue}
            items={AVSRespCodes}
            className='uid-avsresp-select-vt'
            label={createFilterLabel(criteria)}
            placeHolder={'-- AVS --'}
          />
        )
      case 'CSC':
        return (
          <InputSelect
            handleSelectionChange={(evt) => {
              onSelectionChange(evt.val)
            }}
            selectedItem={criteria?.searchValue}
            items={CSCRespCodes}
            className='uid-cscresp-select-vt'
            label={createFilterLabel(criteria)}
            placeHolder={'-- CSC --'}
          />
        )
      default:
        // this is the general select filter and there can only be one.
        return (
          <div className="custom-input uid-general-select-vt">
            <input
              name="search-criteria"
              id="query"
              type="text"
              value={criteria.searchValue}
              onChange={(e) => onSelectionChange(e.target.value)}
              placeholder={`Enter ${criteria.searchKey}`}
            />
          </div>
        )
    }
  }

  const getHelpContent = (key: string): string => {
    return SearchFilterHelp[key.toLowerCase()]
  }

  return (
    <Fieldset>
      <Legend title="Search" />

      <form className="view-transactions-form" onSubmit={handleFormSubmit}>
        <If is={Boolean(merchantName)}>
          <div className="view-transactions-merchant-header">
            <p>
              Viewing ALL transactions for merchant&nbsp;
              <strong>{merchantName}</strong>
            </p>
          </div>
        </If>

        <div className="view-transactions-date-filter">
          {
            // problem making this shared component look like the legacy page.
            // If required then might need to update this component to use datepicker.
            // Or adding seconds to standard time input would need "step=1" attribute.
            // Icons would need to hide default ones or use another lib.
            // Might need exact replica of floating windows then need changes.
          }
          <DateTimeRange
            startDate={searchStartDate}
            startTime={searchStartTime}
            endDate={searchEndDate}
            endTime={searchEndTime}
            setStartDate={setSearchStartDate}
            setStartTime={setSearchStartTime}
            setEndDate={setSearchEndDate}
            setEndTime={setSearchEndTime}
          />
        </div>

        <CustomInput className="search-filter">

          <label htmlFor="query">Search For:</label>
          {
            createSearchFilterSelect(searchCriteria, (val) => {
              handleUpdateSearchCriteria(val, 'searchValue')
            })
          }
          <select
            className="input-25"
            name="search-criteria"
            value={searchCriteria?.searchKey}
            onChange={(e) => handleUpdateSearchCriteria(e.target.value, 'searchKey')}
          >
            {/* the main search filter allows general searches */}
            {['General', ...searchOptions].map((opt, index) => {
              return (
                <option key={index} value={opt}>
                  {opt}
                </option>
              )
            })}
          </select>

          <ToolTip enabled={helpEnabled} icon={<HelpIcon/>} text={getHelpContent(searchCriteria.searchKey)} />

        </CustomInput>

        <CustomInput className="">
          <label>Filter by:</label>
          <button className="other-btn ml-0" onClick={handleAddExtraFilter}>
            + Add Filter
          </button>
        </CustomInput>

        {extraSearchCriteria.map((c, searchCritIndex) => {
          return <>
            <CustomInput className="search-filter" key={`extra-criteria-${searchCritIndex}`}>
              <label htmlFor="query">&nbsp;</label>
              {
                createSearchFilterSelect(c, (val) => {
                  handleUpdateExtraCriteria(val, searchCritIndex, 'searchValue')
                })
              }
              <select
                className="input-25"
                name="extra-search-criteria-key"
                value={c.searchKey}
                onChange={(e) =>
                  handleUpdateExtraCriteria(e.target.value, searchCritIndex, 'searchKey')
                }
              >
                {searchOptions.map((opt, optIndex) => {
                  const isAccountOption = opt === 'Account'
                  const optionValue =
                    isAccountOption && c.searchKey === 'or Account'
                      ? 'or Account'
                      : opt

                  return (
                    <option key={`${searchCritIndex}-${optIndex}`} value={opt}>
                      {optionValue}
                    </option>
                  )
                })}
              </select>

              <button
                type="button"
                onClick={(e) => handleRemoveExtraFilter(e, searchCritIndex)}
                style={{
                  color: 'gray',
                  background: 'none',
                  border: 'none',
                  fontSize: '12px',
                  cursor: 'pointer',
                  marginLeft: '8px',
                  padding: '0px',
                }}
              >
                ✕
              </button>

              <ToolTip enabled={helpEnabled} icon={<HelpIcon/>} text={getHelpContent(c.searchKey)} />

            </CustomInput>
          </>
        })}

        <SearchButton />
      </form>
    </Fieldset>
  )
}
